var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { AllowedPlacements, isString } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import '../chip/chip';
import '../dropdown-panel/dropdown-panel';
import '../field-note/field-note';
import '../icon/icons/chevron-down';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../search-form/search-form';
import '../skeleton/skeleton';
import '../tooltip-label-overflow/tooltip-label-overflow';
import styles from './dropdown.styles';
/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot field-note - Slot for field-note
 * @slot error - slot for error note
 * @slot warning - slot for warning note
 * @fires bl-dropdown-open - emitted when dropdown panel is opened
 * @fires bl-dropdown-close - emitted when dropdown panel is closed
 * @fires bl-dropdown-multi-select - emits when checkbox in multiselect is checked
 * @fires bl-change - emitted when value is changed in typeahead variant => Emits null when clear button is clicked . Emits empty string when users clears using keydown.
 * @fires bl-scroll-end - emitted according to scrollOffset  values, by default it is zero so emitted at the end of dropdown panel.
 * @csspart base - The component's base wrapper.
 * @csspart label-loader - The component's label loader.
 * @csspart label-skeleton - The component's label skeleton.
 * @csspart body-loader - The component's body loader.
 * @csspart body-skeleton - The component's body skeleton.
 * @csspart field-note-loader - The component's field-note-loader.
 * @csspart field-note-skeleton-wrap - The component's field-note-skeleton wrapper.
 * @csspart field-note-skeleton - The component's field-note-skeleton.
 * @csspart label-wrapper - The component's label wrapper.
 * @csspart part-label - The component's label slot.
 * @csspart part-required - The component's required slot.
 * @csspart container - The component's container wrapper.
 * @csspart header-content - The component's header content.
 * @csspart body - The component's body wrapper.
 * @csspart input-wrap - The component's input wrapper.
 * @csspart select - The component's `input` element.
 * @csspart dropdown-before-icon - The component's dropdown-before-icon wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart text-container - The component's text-container.
 * @csspart chip - The component's `cat-chip` for multiselect.
 * @csspart text - The component's text of placeholder/value.
 * @csspart icon-wrapper - The component's icon wrapper.
 * @csspart cross-icon - The component's cross icon button.
 * @csspart icon-after - The component's `<cat-icon-close>` icon.
 * @csspart separator - The component's separator wrapper.
 * @csspart separator-line - The component's separator line.
 * @csspart status-icon - The component's status icon wrapper.
 * @csspart chevron-down-button - The component's chevron button.
 * @csspart arrow-icon - The component's `<cat-icon-chevron-down>`.
 * @csspart panel-component - The component's `<cat-dropdown-panel>`.
 * @csspart part-default - The component's default slot.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-error - The component's error slot.
 * @csspart warning - The component's warning slot.
 */
export class BlocksDropdown extends BlocksElement {
    static { this.formAssociated = true; }
    static { this.styles = styles; }
    /**
     * Query the checkbox list
     */
    get _checkboxList() {
        return this.querySelectorAll('cat-checkbox-field');
    }
    get inputValue() {
        return this.dropdownInput?.value;
    }
    /**
     * Associates the control with a form element
     */
    get form() {
        return this.internals_?.form;
    }
    get isNotEmptyArray() {
        return Array.isArray(this.value) && this.value.length > 0;
    }
    /**
     *
     * Generates and returns a string indicating the dropdown behavior
     * for accessibility purposes, specifically for screen reader announcements.
     * The descriptions are designed to inform users about the nature of the dropdown,
     * whether it's read-only, supports multi-selection, or both. This helps users
     * navigate and understand the UI better, especially those using assistive technologies.
     *
     * 1)Check if the dropdown is read-only but does not support multi-selection
     * 2)Check if the dropdown supports multi-selection but is not read-only. Determine the number of
     * selected items and set the role description accordingly
     */
    get roleDescriptionText() {
        const comboBoxRole = 'combo box collapsed opens list';
        if (this.isReadonly && !this.isMultiSelect) {
            return comboBoxRole; /* 1 */
        }
        else if (this.isMultiSelect) {
            return this.value?.length ? `${this.value.length} items selected multiselect combo box` : '0 items selected multiselect combo box'; /* 2 */
        }
    }
    /**
     * Creates and returns a string displaying the count of selected items for display in a dropdown chip.
     * Provides a visual cue to users about the number of selections made.
     */
    get chipText() {
        return this.value?.length ? `${this.value.length} items selected` : '';
    }
    /**
     * Query all the cat-list-items
     */
    get getCatListItems() {
        return Array.from(this.getCatList()?.querySelectorAll('cat-list-item') ?? []);
    }
    /**
     * Query all the cat-checkbox-field-items
     */
    get getCheckboxFieldItems() {
        return Array.from(this.getFieldset()?.querySelectorAll('cat-checkbox-field') ?? []);
    }
    get isPopoverEnabled() {
        return this.isPopoverSupported && !this.isInline;
    }
    get _catDropdownPanelBody() {
        return this.shadowRoot?.querySelector('cat-dropdown-panel')?.shadowRoot?.querySelector('.cat-c-dropdown-panel__body');
    }
    get hasBeforeSlot() {
        return this.slots.hasSlotted('before');
    }
    get hasFieldNoteSlot() {
        return this.slots.hasSlotted('field-note');
    }
    get hasWarningSlot() {
        return this.slots.hasSlotted('warning');
    }
    get hasErrorSlot() {
        return this.slots.hasSlotted('error');
    }
    /**
     * Constructor function
     * 1) Autogenerate the fieldID
     */
    constructor() {
        super();
        this.autoPositionController = new AutoPositionController(this);
        /**
         * Size
         * <cat-text-passage size="sm">
         * <ul>
         * <li>**lg** increases the dropdown height with label inside</li>
         * <li>**md** reduce the dropdown height with label outside </li>
         * <li>**sm** reduce the dropdown height by removing label </li>
         * </ul>
         * </cat-text-passage>
         */
        this.size = 'lg';
        /**
         * is Active
         * 1) Dropdown is open when set to true. Close when set to false
         */
        this.isActive = false;
        /**
         * When true, displays the result message via slot.
         * Typically used to show messages like "No results found" when there are no items to display.
         */
        this.displayResultMessage = false;
        /**
         * The select field's label
         */
        this.label = 'Label';
        /**
         * Notation is used for setting required or optional label
         */
        this.notation = '(required)';
        /**
         * Clear button text, To be used only for accessibility
         */
        this.clearButtonText = 'Clear text';
        /**
         * The select field's required attribute
         */
        this.required = false;
        /**
         * **Dropdown position**
         * - **bottom** Dropdown panel appears on the bottom
         * - **top** Dropdown panel appears on the top
         * If `isInline` is false, and if specified position doesn't fit inside the viewport,
         * it will try to accommodate in other positions which fits best.
         */
        this.position = 'auto';
        /**
         * Determines the behavior of the panel based on the value of the `isInline` property.
         * If `isInline` is false and the popover attribute is supported by the browser,
         * the panel will be opened inside the top layer.
         * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
         * If `isInline` is true, the popover attribute will not be used, and the panel
         * will use the floating UI absolute strategy.
         *  @property {Boolean} isInline - Default is false.
         */
        this.isInline = false;
        /**
         * Enables the readonly on input field
         */
        this.isReadonly = false;
        /**
         * Autocomplete property, by default it would be on
         */
        this.autocomplete = 'on';
        /**
         * Delay for show tooltip when label is overflowing
         */
        this.tooltipShowDelay = 0;
        /**
         * Delay for hide tooltip when label is overflowing
         */
        this.tooltipHideDelay = 0;
        /**
         * Hide the label overflow tooltip
         */
        this.hideLabelTooltip = false;
        /**
         * Name or Title for the warning icon
         * meant for accessibility purposes only
         */
        this.warningIconTitle = 'warning icon';
        /**
         * Name or Title for the error icon
         * meant for accessibility purposes only
         */
        this.errorIconTitle = 'error icon';
        /**
         * The ARIA live region politeness level for the multiselect dropdown when checkbox is
         * selected / unselected.
         *
         * @type {string}
         * @default "polite"
         */
        this.arialive = 'polite';
        /**
         * Specifies the bottom offset for the dropdown panel to determine when lazy loading should be triggered.
         * The value is a string representing the bottom offset in the format 'px' or '%'.
         * Default value is '0px'.
         */
        this.scrollOffset = '0px';
        /**
         * state to set the lists are empty
         */
        this.isEmpty = false;
        /**
         * To set inputChange in single selection typeahead
         */
        this.inputChange = '';
        this.internals_ = this.attachInternals?.();
        this.wrapperElRef = createRef();
        this.isPopoverSupported = isPopoverSupported();
        /**
         * Identify the current focused index
         */
        this.currentFocusedIndex = -1;
        this.slots = new SlotController(this, null, 'before', 'field-note', 'error', 'warning');
        /**
         * Handle click outside the component
         * 1) Close the show hide panel on click outside
         * 2) If the nav is already closed then we don't care about outside clicks and we
         * can bail early
         * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
         * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
         * undefined. To work around that we'll check that we have a shadowRoot (and a
         * rendered .host) element here to appease the TypeScript compiler. This should never
         * actually be shown or run for a human end user.
         * 4) Check to see if we clicked inside the active panel
         * 5) If the panel is active and we've clicked outside of the panel then it should
         * be closed and clear the input value.
         */
        this.handleOnClickOutside = (event) => {
            /* 2 */
            if (!this.isActive && !this.isTypeahead) {
                return;
            }
            /* 3 */
            if (!this.shadowRoot?.host) {
                throw Error('Could not determine panel context during click handler');
            }
            /* 4 */
            const didClickInside = event.composedPath().includes(this.shadowRoot.host);
            /* 5 */
            if (!didClickInside) {
                if (this.isActive) {
                    this.toggleActive();
                    this.dispatchDrawerEvent();
                }
                this.clearInput(true);
                if (this.isTypeahead && !this.isMultiSelect) {
                    this.makeSelectedItemActive();
                }
            }
        };
        /*
         * Scroll to view if any items is hiding behind the the dropdown panel
         * 1) No scroll position for an element then no action needed
         * 2) For multiselect in case item top, height values is greater that dropdown panel then scroll in to view. Seems like value 2 is necessary to accommodate the space
         * 3) Single select - in case scroll element hidden behind the dropdown panel, the scroll in to view. Added value 5 for accommodating the overall calculation of top and height.
         */
        this.scrollToView = (nextItemElement) => {
            const { top: scrollItemTop, height: scrollItemHeight } = nextItemElement.getBoundingClientRect();
            const boundingClientRect = this._dropdownPanel?.getBoundingClientRect();
            if (boundingClientRect) {
                const { top: dropdownPanelTop, height: dropdownPanelHeight } = boundingClientRect;
                const { clientHeight: dropdownPanelClientHeight } = this._dropdownPanel;
                /* 1 */
                if (scrollItemTop === undefined || scrollItemHeight === undefined) {
                    return;
                }
                /* 2 */
                if (this.isMultiSelect) {
                    if (Math.floor(scrollItemTop - dropdownPanelHeight + scrollItemHeight + 5) >= Math.floor(dropdownPanelTop)) {
                        nextItemElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
                    }
                    if (scrollItemTop - 5 <= dropdownPanelTop) {
                        nextItemElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
                    }
                    return;
                }
                /* 3 */
                if (scrollItemTop - dropdownPanelTop + scrollItemHeight + 2 > dropdownPanelClientHeight) {
                    nextItemElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
                }
                if (dropdownPanelTop + 2 > scrollItemTop) {
                    nextItemElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
                }
            }
        };
        /*
         * 1) For the down arrow, get the next checkbox list item, focus on that item. If already a focused index value available, then get the next item based this value
         * 2) For the down arrow, get the last item index the focus on that item.
         */
        this.focusOnMultiListItem = (e) => {
            e.preventDefault();
            const nonDisabledCheckBoxItems = (Array.from(this._checkboxList) || []).filter((item) => !item.disabled || item.hasHover);
            let checkboxListItem;
            if (e.key === KEYS.DOWN_ARROW) {
                const nextItemIndex = this.currentFocusedIndex === nonDisabledCheckBoxItems.length - 1 ? 0 : this.currentFocusedIndex + 1;
                checkboxListItem = nonDisabledCheckBoxItems[nextItemIndex];
                checkboxListItem?.focus();
                nextItemIndex === 0 && this.updateScroll(e);
                this.currentFocusedIndex = nextItemIndex;
            }
            if (e.key === KEYS.UP_ARROW) {
                const nextItemIndex = this.currentFocusedIndex === -1 || this.currentFocusedIndex === 0 ? nonDisabledCheckBoxItems.length - 1 : this.currentFocusedIndex - 1;
                checkboxListItem = nonDisabledCheckBoxItems[nextItemIndex];
                checkboxListItem?.focus();
                nextItemIndex === nonDisabledCheckBoxItems.length - 1 && this.updateScroll(e);
                this.currentFocusedIndex = nextItemIndex;
                if (this.currentFocusedIndex === nonDisabledCheckBoxItems.length - 1) {
                    this.lastItemInDropdownPanel();
                }
            }
            if (checkboxListItem) {
                this.scrollToView(checkboxListItem);
            }
        };
        /*
         * In case of multiselect, call the function to focus on first checkbox item else select the list item
         */
        this.onFocusNextItem = (e) => {
            if (this.isMultiSelect) {
                this.focusOnMultiListItem(e);
                return;
            }
            this.focusOnNextListItem(e);
        };
        /**
         * Change output binding
         * 1) If the input field is not readonly, then allow typing
         * 2) Clear the label & value's to show the filling text
         * 3) Set the dropdown to active while filling
         */
        this.handleOnChange = (event) => {
            const input = event.target;
            if (this.isTypeahead) {
                this.inputChange = input.value;
                /**
                 * 1. Show results panel when no min char length is set
                 * 2. Show results panel when input length is greater than min char length expected
                 */
                if (!this.minCharacterLength || input.value.length >= this.minCharacterLength) {
                    if (!this.isActive) {
                        this.toggleActive();
                        this.dispatchDrawerEvent();
                    }
                }
                // hide results panel when input length is lesser than min char length expected
                if (this.minCharacterLength && input.value.length < this.minCharacterLength) {
                    this.toggleActive();
                    this.dispatchDrawerEvent();
                }
                //To keep selected item as selcted after search/re-rendering.
                if (!this.isMultiSelect) {
                    this.makeSelectedItemActive();
                }
                this.assignSearchText(input.value);
                if (input.value === '') {
                    this.clearSearchField();
                }
                this.emitOnChangeEvent(event.target.value);
            }
        };
        this.ariaControlsId = this.ariaControlsId || nanoid();
        this.fieldId = this.fieldId || nanoid(); /* 1 */
        this.labelId = nanoid();
    }
    getFloatingElement() {
        return this._dropdownPanel;
    }
    getSync() {
        return 'width';
    }
    getPosition() {
        return this.position;
    }
    getIsInline() {
        return this.isInline;
    }
    getReferenceElement() {
        return this.dropdownBody;
    }
    getOverflowElement() {
        return this._catDropdownPanelBody;
    }
    getAllowedPlacements() {
        return AllowedPlacements;
    }
    /**
     * Connected callback lifecycle
     * 1) Close dropdown panel when you click outside of the element
     */
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('mousedown', this.handleOnClickOutside, false); /* 1 */
        this.ariaLabelledBy = nanoid();
        this.textWrapperId = nanoid();
    }
    /**
     * Disconnected callback lifecycle
     * 1) Remove event listeners
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    }
    /**
     * Clear the manually typed text in input element
     */
    clearInput(emitEvent) {
        if (this.isTypeahead && this.inputChange.length) {
            this.inputChange = '';
            if (emitEvent) {
                this.emitOnChangeEvent(this.getInputValue());
            }
        }
    }
    /**
     * Set menu active state
     * 1) Toggle the active state between true and false
     * 2) for typeahead and default dropdown
     */
    toggleActive(event) {
        const value = event?.detail?.value;
        let currentSelectedListItem;
        if (event) {
            currentSelectedListItem = event.target.closest('cat-list-item');
        }
        if (!this.minCharacterLength) {
            this.isActive = !this.isActive; /* 1 */
        }
        else {
            if (this.inputValue?.length >= this.minCharacterLength && !this.isActive) {
                this.isActive = true;
            }
            else if (this.isActive) {
                this.isActive = false;
            }
        }
        /* 2 */
        let selectedOptionLabel;
        if (currentSelectedListItem) {
            selectedOptionLabel = this.getSelectedItemLabel(currentSelectedListItem, value);
        }
        if (selectedOptionLabel && !this.isMultiSelect) {
            //If it its typeahead store this value in temp variable so that when we clear we can reset it back to previous selected value
            this.clearInput();
            this.selectedItemLabel = selectedOptionLabel;
            this.makeSelectedItemActive(currentSelectedListItem);
        }
        if (!this.isMultiSelect) {
            this.selectedItemLabel = selectedOptionLabel || this.selectedItemLabel;
        }
        if (this.isActive === true) {
            if (!this.hideLabelTooltip && this._tooltipLabelOverflow) {
                this._tooltipLabelOverflow.enableTooltip = false;
            }
            if (this.isTypeahead) {
                //TODO
                //If it is typeahead we should highlight whatever value there in the input field
                this.assignSearchText(this.inputValue);
            }
        }
        else {
            if (!this.hideLabelTooltip && this._tooltipLabelOverflow) {
                this._tooltipLabelOverflow.enableTooltip = true;
            }
            this.hidePopup();
        }
    }
    /**
     * Emits dropdown open/close event
     */
    dispatchDrawerEvent() {
        this.isActive
            ? this.dispatch({ eventName: 'bl-dropdown-open', detailObj: { isActive: true } })
            : this.dispatch({ eventName: 'bl-dropdown-close', detailObj: { isActive: false } });
    }
    /***
     * Set single select dropdown value on initial load in updated method
     */
    setSingleSelectDropdownValue(value) {
        if (value === '') {
            this.clearSearchField();
        }
        else {
            setTimeout(() => {
                const items = this.getCatListItems;
                const activeItem = items.find((list) => list.isActive);
                if (activeItem) {
                    activeItem.isActive = false;
                    activeItem.isCheckIcon = false;
                }
                const currentSelectedItem = items.find((element) => {
                    if (element?.value) {
                        const selectedItemValue = this.getSelectedItemValue(element?.value, 'value');
                        if (value.toLowerCase() === selectedItemValue?.toLowerCase()) {
                            this.selectedItemLabel = this.getSelectedItemLabel(element, element?.value);
                            this.clearInput();
                            return element;
                        }
                    }
                    return null;
                });
                if (currentSelectedItem) {
                    currentSelectedItem.isActive = true;
                    currentSelectedItem.isCheckIcon = true;
                }
            }, 400);
        }
    }
    /** Handle Active
     * 1) Position the dropdown panel based on the available room in the viewport
     * 2) If the active state is turned on,then set focus to selected item which automatically sets scroll position to the selected item.
     */
    handleActive() {
        this.autoPositionController.openOverlay();
        if (!this.isMultiSelect) {
            const allListItem = this.getCatListItems;
            const activeItem = allListItem.find((list) => list.isActive);
            if (activeItem) {
                this.updateComplete.then(() => {
                    this.scrollToView(activeItem);
                });
            }
            else if (this._dropdownPanel) {
                this._dropdownPanel.scrollTop = 0;
            }
        }
        if (!this.isMultiSelect && !this.isTypeahead && isString(this.value)) {
            this.setSingleSelectDropdownValue(this.value);
        }
        else if (this.isMultiSelect) {
            const element = this._dropdownPanel?.shadowRoot?.querySelector('.cat-c-dropdown-panel__body');
            if (element) {
                element.scrollTop = 0;
            }
        }
    }
    /**
     * Hide calender popup
     * 1. Reset the focus when close the popup
     */
    hidePopup() {
        /* 1 */
        this.currentFocusedIndex = -1;
        this.currentActiveElement = undefined;
        /* 2 */
        this.autoPositionController.closeOverlay();
    }
    /**
     * Update the scroll of the dropdown panel body on Arrow up/down key press
     * If down arrow key pressed then scroll to top, if the up arrow key pressed then scroll to bottom To make the first/last item visible
     */
    updateScroll(e) {
        e.preventDefault();
        const panelBody = this._dropdownPanel?.shadowRoot?.querySelector('.cat-c-dropdown-panel');
        if (panelBody && this._dropdownPanel) {
            this._dropdownPanel.scrollTop = e.key === KEYS.DOWN_ARROW ? this._dropdownPanel.scrollTop : this._dropdownPanel.scrollHeight;
        }
    }
    /**
     * Get all the non disabled list item
     */
    getNonDisabledListItems() {
        const allListItem = this.getCatListItems;
        return allListItem.filter((item) => !item.isStatic);
    }
    /**
     * Get active list item
     */
    getActiveListItem() {
        const allListItem = this.getCatListItems;
        return allListItem.find((item) => item.isActive);
    }
    /*
     * Focus the first list item
     */
    focusOnFistListItem(e) {
        e.preventDefault();
        const firstNonDisabledListItem = Array.from(this.getNonDisabledListItems() || [])[0];
        firstNonDisabledListItem?.focus();
        this.currentActiveElement = firstNonDisabledListItem;
    }
    /*
     * Focus the last list item
     */
    focusOnLastListItem(e) {
        e.preventDefault();
        const lastNonDisabledListItem = Array.from(this.getNonDisabledListItems() || []).pop();
        lastNonDisabledListItem?.focus();
        this.currentActiveElement = lastNonDisabledListItem;
    }
    /**
     * Get the next active element
     */
    getNextActiveElement(el, e) {
        let nextElement = e.key === KEYS.DOWN_ARROW ? el.nextElementSibling : el.previousElementSibling;
        let nextActiveElement = !nextElement?.isStatic ? nextElement : undefined;
        if (nextActiveElement) {
            return nextActiveElement;
        }
        while (nextElement?.isStatic) {
            nextElement =
                e.key === KEYS.DOWN_ARROW ? nextElement.nextElementSibling : nextElement.previousElementSibling;
            nextActiveElement = !nextElement.isStatic ? nextElement : undefined;
            if (!nextElement || nextActiveElement) {
                break;
            }
        }
        return nextActiveElement;
    }
    lastItemInDropdownPanel() {
        const dropdownPanel = this._dropdownPanel;
        dropdownPanel.keyboardLastItemFocus();
    }
    /**
     * Focus on next list item
     */
    focusOnNextListItem(e) {
        e.preventDefault();
        const activeElement = this.currentActiveElement || this.getActiveListItem();
        let nextActiveElement;
        if (activeElement) {
            nextActiveElement = this.getNextActiveElement(activeElement, e);
            nextActiveElement?.focus();
            this.currentActiveElement = nextActiveElement;
        }
        if (!this.currentActiveElement) {
            if (e.key === KEYS.DOWN_ARROW) {
                this.focusOnFistListItem(e);
            }
            if (e.key === KEYS.UP_ARROW) {
                this.focusOnLastListItem(e);
                this.lastItemInDropdownPanel();
            }
        }
    }
    /**
     * Handle on select input keydown
     * 1) If key selected is enter  toggle the menu open/close, except for typeahead
     * 2) If the panel is open, pressed the escape or tab then close the panel and focus on the triggered input
     * 3) If tab is pressed and dropdown panel is not opened, clear the input field value after 400ms, so that this
          will be called after the value is set
     * 4) If arrow key is pressed then focus on the next/previous element
     */
    handleSelectKeyDown(e) {
        /* 1 */
        if ((e.key === KEYS.ENTER || e.key === KEYS.SPACE) && this.isReadonly === false) {
            this.toggleActive();
            this.dispatchDrawerEvent();
        }
        /* 2 */
        if (e.key === KEYS.ESCAPE && this.isActive) {
            e.preventDefault();
            this.toggleActive();
            this.dispatchDrawerEvent();
            this.focus();
        }
        /* 3 */
        if (e.key === KEYS.TAB) {
            if (this.isActive) {
                e.preventDefault();
                this.toggleActive();
                this.dispatchDrawerEvent();
                this.focus();
            }
            else {
                setTimeout(() => {
                    this.clearInput(true); /* 3 */
                }, 400);
            }
        }
        /* 4 */
        if (e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW) {
            e.preventDefault();
            this.onFocusNextItem(e);
        }
    }
    /**
     * Toggle the dropdown panel on click of anywhere in dropdown
     */
    handleContainerDown(e) {
        e.preventDefault();
        if (!this.isReadonly) {
            this.focus();
            this.toggleActive();
            this.dispatchDrawerEvent();
        }
    }
    /**
     * Handle on keydown
     * 1) If panel is open and escape is pressed, close the menu and return focus to the trigger input
     * 2) If panel is open, arrow key is pressed then focus on the next/previous element only in case of multiselect or with search.
     * 3) Find the last item in the list. Set the last element to that item to define variable
     * 4) If the last element is defined, once Tab is selected after that the panel will close
     */
    handleKeyDown(e) {
        /* 1 */
        if (this.isActive === true && (e.key === KEYS.ESCAPE || e.key === KEYS.TAB)) {
            e.preventDefault();
            this.toggleActive();
            this.dispatchDrawerEvent();
            this.focus();
        }
        /* 2 */
        if (this.isActive === true && (e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW)) {
            e.preventDefault();
            this.onFocusNextItem(e);
        }
        const catListItems = this.getCatListItems;
        if (catListItems.length > 0) {
            let lastFocusableElement;
            for (let i = catListItems.length - 1; i >= 0; i--) {
                if (!catListItems[i].shadowRoot?.querySelector('.cat-c-list-item.cat-is-disabled')) {
                    lastFocusableElement = catListItems[i];
                    break;
                }
            }
            let lastElement;
            if (document.activeElement === lastFocusableElement) {
                lastElement = lastFocusableElement; /* 3 */
            }
            if (lastElement) {
                if (this.isActive === true && e.key === KEYS.TAB) {
                    this.toggleActive(); /* 4 */
                    this.dispatchDrawerEvent();
                }
            }
        }
        const checkboxFieldItems = this.querySelectorAll('cat-checkbox-field');
        if (checkboxFieldItems) {
            let lastFocusableElement;
            for (let i = checkboxFieldItems.length - 1; i >= 0; i--) {
                if (!checkboxFieldItems[i].shadowRoot?.querySelector('.cat-c-checkbox-field.cat-is-disabled') || checkboxFieldItems[i].hasHover) {
                    lastFocusableElement = checkboxFieldItems[i];
                    break;
                }
            }
            let lastElement;
            if (document.activeElement === lastFocusableElement) {
                lastElement = lastFocusableElement; /* 5 */
            }
            if (lastElement) {
                if (this.isActive === true && e.key === KEYS.TAB) {
                    this.toggleActive(); /* 6 */
                    this.dispatchDrawerEvent();
                }
            }
        }
    }
    /**
     * Handle auto expand(isActive : true default)
     */
    firstUpdated() {
        this.makeSelectedItemActive();
    }
    updated(_changedProperties) {
        if (_changedProperties.has('value')) {
            if (this.isSelectValueObject(this.value) && !this.isMultiSelect) {
                this.value = this.value.value;
            }
            if (!this.isMultiSelect && !this.isTypeahead && isString(this.value)) {
                this.setSingleSelectDropdownValue(this.value);
            }
            if (Array.isArray(this.value) && !this.value.length) {
                this.unSelectAllItems();
            }
            if (!this.isMultiSelect && this.isTypeahead && isString(this.value)) {
                this.setSingleSelectDropdownValue(this.value);
            }
            /**
             * TODO: Array of values to be checked on form submit with action - query param
             */
            const formValue = isString(this.value) ? this.value : this.value ? JSON.stringify(this.value) : null;
            this.internals_?.setFormValue(formValue);
        }
        if (_changedProperties.has('autoFocus') && this.autoFocus) {
            this.focus();
        }
        if (_changedProperties.has('isActive')) {
            if (this.isActive) {
                this.handleActive();
            }
        }
    }
    focus() {
        this.dropdownInput?.focus();
    }
    /**
     * function to check and return a value If value is type of selectValue.
     * @param value
     * @returns value of value
     */
    isSelectValueObject(value) {
        return typeof value === 'object' && value !== null && 'value' in value;
    }
    /** Check if cat-list is in a slot and set the catList var */
    getCatList() {
        let catList;
        if (this.querySelector('cat-list')) {
            catList = this.querySelector('cat-list');
            return catList;
        }
        else if (this.querySelector('slot')) {
            const nodes = this.querySelector('slot')?.assignedNodes({ flatten: false });
            if (nodes) {
                catList = nodes[0];
                return catList;
            }
        }
    }
    /** Check if cat-fieldset is in a slot and set the catFieldset var */
    getFieldset() {
        let catFieldset;
        if (this.querySelector('cat-fieldset')) {
            catFieldset = this.querySelector('cat-fieldset');
            return catFieldset;
        }
        else if (this.querySelector('slot')) {
            const element = this.querySelector('slot');
            if (element) {
                const nodes = element.assignedNodes({ flatten: false });
                catFieldset = nodes[0];
                return catFieldset;
            }
        }
        return;
    }
    /***
     * This functions is used with single selection to clear the previous selection on select new item
     */
    resetAllListItemsToDefault() {
        const elListItems = this.getCatListItems;
        elListItems.forEach((el) => {
            if (el.isActive) {
                el.isActive = false;
            }
        });
        return elListItems;
    }
    /***
     * This functions is used find with item is selected on load with value
     */
    findSelectedItem(elListItems) {
        return elListItems.find((elItem) => {
            if (isString(elItem.value)) {
                return elItem.value === this.value;
            }
            else {
                /***
                 * TODO - check the condition for Object value type
                 * this.value will be string for single select dropdown
                 * */
                return elItem.value?.value === this.value;
            }
        });
    }
    /**
     * First updated lifecycle when component is first updated on page
     * 1) on select of an item, return the focus to the dropdown and set the value with the text-content or label of the selected item
     */
    async makeSelectedItemActive(el) {
        await this.updateComplete;
        /* 1 */
        if (!this.isMultiSelect) {
            setTimeout(() => {
                const elListItems = this.resetAllListItemsToDefault();
                let element;
                if (!el) {
                    element = this.findSelectedItem(elListItems);
                }
                else if (el) {
                    element = el;
                }
                if (element) {
                    this.value = this.getSelectedItemValue(element.value, 'value');
                    this.selectedItemLabel = this.getSelectedItemLabel(element, element.value);
                    if (element.isActive) {
                        // unselect already selected item
                        element.isActive = false;
                    }
                    element.isActive = true;
                    element.isCheckIcon = true;
                }
            }, 1);
        }
    }
    /**
     * This function returns the value of the selected dropdown option
     * @param value - string or object type
     * @param extract - extract label or value property from the object
     * @returns value - sting type
     */
    getSelectedItemValue(value, extract = 'label') {
        let selectedOptionLabel;
        if (isString(value)) {
            selectedOptionLabel = value;
        }
        else {
            if (extract === 'label') {
                selectedOptionLabel = value?.label ?? '';
            }
            else {
                selectedOptionLabel = value?.value ?? '';
            }
        }
        return selectedOptionLabel;
    }
    /**
     * This function returns the text-content ot label or value of the selected dropdown option
     * @param currentItem - Selected list item
     * @param value - string or object type
     * @param extract - extract label or value property from the object
     * @returns value - sting type
     */
    getSelectedItemLabel(currentItem, value, extract = 'label') {
        let selectedOptionLabel = currentItem?.textContent?.trim();
        if (!selectedOptionLabel) {
            selectedOptionLabel = this.getSelectedItemValue(value, extract);
        }
        return selectedOptionLabel;
    }
    /**
     * Emit onChange event
     */
    emitOnChangeEvent(value) {
        this.dispatch({ eventName: 'bl-change', detailObj: { value: value } });
    }
    /** To assign input value to searchText of list item */
    assignSearchText(input) {
        requestAnimationFrame(() => {
            if (!this.isMultiSelect) {
                const catList = this.getCatList();
                /* 2 */
                if (catList) {
                    const catListItems = catList.querySelectorAll('cat-list-item');
                    if (!this.displayResultMessage) {
                        this.isEmpty = catListItems.length <= 0;
                    }
                    if (catListItems) {
                        catListItems.forEach((element) => {
                            element.searchText = input?.trim();
                        });
                    }
                }
            }
            else if (this._checkboxList.length !== 0) {
                this.isEmpty = false;
                this._checkboxList.forEach((element) => {
                    element.searchText = input?.trim();
                });
            }
            else if (!this.displayResultMessage) {
                this.isEmpty = this._checkboxList.length <= 0;
            }
        });
    }
    /**
     * Clearing checked by chip
     * 1) Query the checked checkbox-field items and change each item state to unchecked state
     * 2) Clear the value
     * 3) After chip close, keep the focus on the trigger input
     */
    handleChipClose() {
        this.selectedItemLabel = '';
        this.value = []; /* 2 */
        this.multiSelectEventDispatch();
        this.focus(); /* 3 */
        if (this.isTypeahead && this.isMultiSelect) {
            this.updateComplete.then(() => {
                this.emitOnChangeEvent(this.inputValue);
                this.assignSearchText(this.inputValue);
            });
        }
    }
    unSelectAllItems() {
        this._checkboxList.forEach((element) => {
            element.checked = false;
        });
    }
    /**
     * This function to handle dropdown value or placeholder
     * @returns placeholder or value text
     */
    handleValue() {
        if (this.isMultiSelect) {
            return this.placeholder;
        }
        return this.selectedItemLabel || this.placeholder;
    }
    /**
     * toggleCheck for multi select
     */
    toggleCheck(ev) {
        const triggeredValue = this.isSelectValueObject(ev?.detail?.value) ? ev?.detail?.value?.value : ev?.detail?.value;
        if (ev?.detail?.checked) {
            /* 2 */
            if (!this.isSelectValueObject(this.value)) {
                this.value = [...(this.value || []), ev.detail.value];
            }
        }
        else {
            this.value = this.value.filter((item) => (typeof item !== 'string' ? (isString(item.value) ? item.value : item.value?.value) : item) !== triggeredValue);
        }
        this.multiSelectEventDispatch();
    }
    /**
     * set roles to list and list item for accessibility
     */
    setListRoles() {
        if (this.getCatList()) {
            const list = this.getCatList();
            if (list) {
                // This will set the role to 'ul' element which announces the position correctly in nvda
                list.listRole = 'listbox';
                // This will set the role to host element which announces the position correctly in voiceover
                list.setAttribute('role', 'listbox');
                list.arialabel = this.label;
            }
            this.getCatListItems.forEach((item) => {
                item.listItemRole = 'option';
            });
            return;
        }
        /**
         * set roles to fieldSet and checkbox-field item for accessibility
         */
        if (this.getFieldset() && this.isMultiSelect) {
            const fieldSet = this.getFieldset();
            if (fieldSet) {
                fieldSet.fieldsetRole = 'listbox';
                fieldSet.arialabel = this.label;
            }
            this.getCheckboxFieldItems.forEach((item) => {
                item.roleForCheckboxField = 'option';
            });
        }
    }
    /** To clear input box and close dropdown on click of cross icon */
    clearSearchField() {
        if (isString(this.value) && this.value?.length !== 0) {
            this.value = '';
        }
        if (this.dropdownInput) {
            this.dropdownInput.value = '';
        }
        this.inputChange = '';
        this.selectedItemLabel = '';
        this.makeSelectedItemActive();
    }
    /**
     * Dispatch event for multiselect
     */
    multiSelectEventDispatch() {
        this.dispatch({
            eventName: 'bl-dropdown-multi-select',
            detailObj: { values: this.value }
        });
    }
    /**
     * Set input element value
     * 1) If it is a typeahead and not a multiselect variant, set input value to inputChange
     * 2) If inputChange is not available , set input value to  selectedItemLabel
     * 3) If none of them is true, set input value as empty string
     */
    getInputValue() {
        if (this.isTypeahead && !this.isMultiSelect) {
            if (this.inputChange) {
                return this.inputChange; /* 1 */
            }
            else if (this.selectedItemLabel) {
                return this.selectedItemLabel; /* 2 */
            }
        }
        return this.inputChange; /* 3 */
    }
    /**
     * Check to display tooltip for label overflow
     */
    isOverflowTooltipDisplay() {
        return !this.hideLabel && this.size === 'lg' && !this.hideLabelTooltip && !this.disabled;
    }
    renderLoading(componentClassNames) {
        return html `
      <div part="base" class="${componentClassNames}">
        ${this.size === 'md' && !this.hideLabel
            ? html `<div part="label-loader" class="cat-c-dropdown__label-loader">
              <cat-skeleton
                part="label-skeleton"
                exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                class="class3"
                size="sm"
                width="128px"
              ></cat-skeleton>
            </div>`
            : html `${nothing}`}
        <div part="body-loader" class="cat-c-dropdown__input-loader">
          <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
          <cat-icon-chevron-down
            part="icon-chevron-down"
            exportparts=${this.getExportParts('chevron-down', 'icon')}
            class="cat-c-dropdown__skeleton-arrow"
          ></cat-icon-chevron-down>
        </div>
        ${this.hasFieldNote()
            ? html `
              <div part="field-note-loader" class="cat-c-dropdown__fieldnote-loader">
                ${this.size === 'md'
                ? html `<div part="field-note-skeleton-wrap">
                      <cat-skeleton
                        part="field-note-skeleton"
                        exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                        size="sm"
                        width="80px"
                      ></cat-skeleton>
                    </div>`
                : html `<cat-skeleton
                      part="field-note-skeleton"
                      exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                      size="sm"
                      width="128px"
                    ></cat-skeleton>`}
              </div>
            `
            : nothing}
      </div>
    `;
    }
    renderIcons() {
        return html `<div part="icon-wrapper" class="cat-c-dropdown__icon-wrapper">
      ${this.isTypeahead && (this.selectedItemLabel?.length || this.inputChange.length)
            ? html `
            ${this.getCloseButton()}
            <div part="separator" class="cat-c-dropdown__icon">
              <div part="separator-line" class="cat-c-dropdown__seperator-line"></div>
            </div>
          `
            : nothing}
      ${this.status === 'error'
            ? html `<cat-icon-warning-diamond-filled
            part="status-icon"
            exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
            color="error"
            .iconTitle=${this.errorIconTitle}
            class="cat-c-dropdown__icon"
          ></cat-icon-warning-diamond-filled>`
            : nothing}
      ${this.status === 'warning'
            ? html ` <cat-icon-warning-triangle-filled
            part="status-icon"
            exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
            color="knockout-brand"
            class="cat-c-dropdown__icon"
            .iconTitle=${this.warningIconTitle}
          ></cat-icon-warning-triangle-filled>`
            : nothing}
      ${this.isTypeahead && !this.disabled && !this.isReadonly ? this.getChevronButton() : this.getChevronIcon()}
    </div>`;
    }
    getCloseButton() {
        return html `<cat-button
      part="cross-icon"
      exportparts=${this.getExportParts('button-cross-icon', 'button')}
      class="cat-c-dropdown__cross-icon cat-c-dropdown__icon"
      @click=${() => {
            this.clearSearchField();
            this.emitOnChangeEvent(this.inputValue);
        }}
      ?hideText=${true}
      variant="ghost"
      size="xs"
      ?skipTab=${!this.selectedItemLabel && this.inputChange.length ? true : false}
    >
      ${this.clearButtonText}
      <cat-icon-close
        part="icon-after"
        exportparts=${this.getExportParts('close', 'icon')}
        slot="after"
        class="cat-c-dropdown__cross-icon-size"
      ></cat-icon-close>
    </cat-button>`;
    }
    /**
     * The button has been removed from the tab sequence of the page,
     * but is still important to assistive technologies for mobile devices
     * that use touch events to open the list of options.
     * https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-none/
     */
    getChevronButton() {
        return html `<cat-button
      part="chevron-down-button"
      exportparts=${this.getExportParts('button-chevron-down', 'button')}
      class="cat-c-dropdown__chevron-down-button"
      ?expanded="${this.isActive}"
      ?skipTab=${true}
      variant="ghost"
      size="xs"
      ?hideText=${true}
    >
      ${this.chevronButtonLabel ?? (this.isActive ? 'Close dropdown' : 'Open dropdown')} ${this.getChevronIcon()}
    </cat-button>`;
    }
    getChevronIcon() {
        return html `<cat-icon-chevron-down
      part="arrow-icon"
      exportparts=${this.getExportParts('chevron-down', 'icon')}
      slot="after"
      class="cat-c-dropdown__arrow"
      size="sm"
    ></cat-icon-chevron-down>`;
    }
    addLabel() {
        return html ` <label
      part="label-wrapper"
      id="${ifDefined(this.labelId)}"
      class="cat-c-dropdown__label"
      aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
      @click=${this.size === 'md' ? this.handleContainerDown : nothing}
    >
      <span part="part-label" class="cat-c-textarea-field--label" id=${ifDefined(this.ariaLabelledBy)}> ${this.label}</span>
      ${this.required ? html `<span part="part-required" class="cat-c-dropdown__required">${this.notation}</span>` : nothing}
    </label>`;
    }
    hasFieldNote() {
        return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
    }
    setAriaDescribedBy() {
        if (this.hasFieldNote()) {
            this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
        }
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-dropdown', {
            'cat-c-dropdown--disabled': this.disabled,
            'cat-c-dropdown--required': this.required,
            'cat-c-dropdown--large': this.size === 'lg',
            'cat-c-dropdown--medium': this.size === 'md',
            'cat-c-dropdown--small': this.size === 'sm',
            'cat-is-error': this.status === 'error',
            'cat-is-warning': this.status === 'warning',
            'cat-is-active': this.isActive === true,
            'cat-is-typeahead': this.isTypeahead === true,
            'cat-is-multi-select-with-checkedlist': this.isMultiSelect === true && this.isNotEmptyArray,
            'cat-has-value': this.value,
            'cat-is-dynamic-width': this.isDynamicWidth === true,
            'cat-hidden-label': this.hideLabel,
            'cat-is-readonly': this.isReadonly === true,
            'cat-has-before': this.hasBeforeSlot,
            'cat-c-dropdown--inline-before': this.slot === 'dropdown',
            'cat-c-dropdown--inline-after': this.slot === 'dropdown-after',
            'cat-c-dropdown--loader': this.isLoading === true,
            'cat-c-dropdown--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
            'cat-c-dropdown--has-warning': this.warningNote || this.hasWarningSlot,
            'cat-c-dropdown--has-error': this.errorNote || this.hasErrorSlot
        });
        if (this.isLoading) {
            return this.renderLoading(componentClassNames);
        }
        const dropdownPanelClassNames = this.componentClassNames('cat-c-dropdown__panel', {
            'cat-c-dropdown__panel-hide': this.isEmpty || !this.isActive || this.disabled || this.isReadonly
        });
        this.setAriaDescribedBy();
        return html `
      <div part="base" class="${componentClassNames}">
        <div part="base" class="${componentClassNames}">
          ${this.size === 'md' ? this.addLabel() : nothing}
          <div part="container" class="cat-c-dropdown__container">
            <div
              part="body"
              class="cat-c-dropdown__body"
              @click=${this.handleContainerDown}
              ${this.isOverflowTooltipDisplay() ? ref(this.wrapperElRef) : nothing}
            >
              ${this.isOverflowTooltipDisplay()
            ? html ` <cat-tooltip-label-overflow
                    tooltipShowDelay=${this.tooltipShowDelay}
                    tooltipHideDelay=${this.tooltipHideDelay}
                    .label=${this.label}
                    .wrapperElementRef=${this.wrapperElRef}
                    .inputElId=${this.fieldId ?? ''}
                  ></cat-tooltip-label-overflow>`
            : nothing}
              ${this.size === 'lg' ? this.addLabel() : nothing}
              <div part="input-wrap" class="cat-c-dropdown__input-wrap">
                <div part="dropdown-before-icon" class="cat-c-dropdown__before-icon">
                  <slot part="part-before" name="before"></slot>
                </div>
                ${!this.isTypeahead
            ? html `
                      <div part="text-container" class="cat-c-dropdown__text-container">
                        <div part="text" class="cat-c-dropdown__text" id=${ifDefined(this.textWrapperId)}>${this.handleValue()}</div>
                      </div>
                    `
            : nothing}
                ${this.isTypeahead || this.isReadonly
            ? html `<input
                      part="select"
                      id="${ifDefined(this.fieldId)}"
                      class="cat-c-dropdown__select"
                      role="${ifDefined(!this.isReadonly ? 'combobox' : undefined)}"
                      name="${ifDefined(this.name)}"
                      placeholder=${ifDefined(this.isTypeahead ? this.placeholder : undefined)}
                      type="text"
                      ?disabled="${this.disabled}"
                      .value=${this.getInputValue()}
                      ?readonly="${this.isReadonly}"
                      aria-expanded="${ifDefined(!this.isReadonly ? (this.isActive ? true : false) : undefined)}"
                      aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
                      aria-labelledby="${ifDefined(this.labelId)} ${ifDefined(this.textWrapperId)}"
                      aria-describedby="${ifDefined(this.ariaDescribedBy)}"
                      aria-roledescription=${ifDefined(this.roleDescription || this.roleDescriptionText)}
                      aria-autocomplete=${ifDefined(this.isTypeahead ? 'list' : 'none')}
                      @keydown=${this.handleSelectKeyDown}
                      @input=${this.handleOnChange}
                      ?autofocus=${this.autoFocus}
                      autocomplete="${this.isTypeahead ? this.autocomplete : 'off'}"
                    />`
            : html `<div
                      part="select"
                      id="${ifDefined(this.fieldId)}"
                      class="cat-c-dropdown__select cat-c-dropdown__select-only"
                      role="combobox"
                      tabindex=${this.disabled ? '-1' : '0'}
                      aria-label="${ifDefined(this.arialabel)}"
                      aria-expanded="${this.isActive ? true : false}"
                      aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
                      aria-labelledby="${ifDefined(this.labelId)} ${ifDefined(this.fieldId)} ${ifDefined(this.textWrapperId)}"
                      aria-describedby="${ifDefined(this.ariaDescribedBy)}"
                      aria-roledescription=${ifDefined(this.roleDescription || this.roleDescriptionText)}
                      aria-autocomplete=${ifDefined(this.isTypeahead ? 'list' : 'none')}
                      @keydown=${this.handleSelectKeyDown}
                    ></div>`}
                ${this.isNotEmptyArray && !this.disabled
            ? html `<cat-chip
                      part="chip"
                      exportparts=${this.getExportParts('chip')}
                      class="cat-c-dropdown__chip"
                      size="sm"
                      @bl-chip-remove=${() => {
                this.handleChipClose();
            }}
                      ?removable=${true}
                      ?isReadonly=${this.isReadonly}
                      .chipTextContent="${this.dropdownChipTextContent || this.chipText}"
                    >
                      ${this.value?.length}
                    </cat-chip>`
            : html `${nothing}`}
                ${this.isMultiSelect && this.isActive && !this.disabled
            ? html `<span class="cat-u-is-vishidden" aria-live=${this.arialive}>${this.selectionStatusText ?? this.chipText}</span>`
            : nothing}
                ${this.renderIcons()}
              </div>
            </div>
            <cat-dropdown-panel
              part="panel-component"
              popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}
              exportparts=${this.getExportParts('dropdown-panel')}
              @keydown=${this.handleKeyDown}
              class="${dropdownPanelClassNames}"
              ?hasScroll=${true}
              id=${ifDefined(this.ariaControlsId)}
              .size=${this.size}
              ?isEmpty=${this.isEmpty}
              .scrollOffset=${this.scrollOffset}
            >
              <slot
                part="part-default"
                @bl-select=${(event) => {
            this.toggleActive(event);
            this.dispatchDrawerEvent();
            this.focus();
        }}
                @bl-checkbox-change=${this.toggleCheck}
                @slotchange=${this.setListRoles}
              ></slot>
            </cat-dropdown-panel>

            <cat-field-note
              part="field-note-wrapper"
              exportparts=${this.getExportParts('field-note')}
              id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
              .status=${this.status}
              ?isDisabled=${this.disabled}
              class="cat-c-dropdown__field-note cat-c-dropdown-fieldnote-slot"
            >
              <slot part="field-note" name="field-note"> ${this.fieldNote} </slot>
            </cat-field-note>
            <cat-field-note
              part="field-note-wrapper"
              exportparts=${this.getExportParts('field-note')}
              id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
              .status=${'error'}
              ?isDisabled=${this.disabled}
              class="cat-c-dropdown__field-note cat-c-dropdown-error-slot"
            >
              <slot part="error" name="error"> ${this.errorNote} </slot>
            </cat-field-note>
            <cat-field-note
              part="field-note-wrapper"
              exportparts=${this.getExportParts('field-note')}
              id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
              ?isDisabled=${this.disabled}
              class="cat-c-dropdown__field-note cat-c-dropdown-warning-slot"
            >
              <slot part="warning" name="warning"> ${this.warningNote} </slot>
            </cat-field-note>
          </div>
        </div>
      </div>
    `;
    }
}
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isActive", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "displayResultMessage", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "fieldId", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "label", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "notation", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "clearButtonText", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "name", void 0);
__decorate([
    property()
], BlocksDropdown.prototype, "value", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "fieldNote", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "errorNote", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "warningNote", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "ariaDescribedBy", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksDropdown.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "status", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "hideLabel", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isMultiSelect", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isLoading", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "position", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isInline", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isDynamicWidth", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "dropdownChipTextContent", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "roleDescription", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isTypeahead", void 0);
__decorate([
    property({ type: Number })
], BlocksDropdown.prototype, "minCharacterLength", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "isReadonly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksDropdown.prototype, "autoFocus", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Number })
], BlocksDropdown.prototype, "tooltipShowDelay", void 0);
__decorate([
    property({ type: Number })
], BlocksDropdown.prototype, "tooltipHideDelay", void 0);
__decorate([
    property({ type: Boolean })
], BlocksDropdown.prototype, "hideLabelTooltip", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "chevronButtonLabel", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "arialabel", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "warningIconTitle", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "errorIconTitle", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "arialive", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "selectionStatusText", void 0);
__decorate([
    property({ type: String })
], BlocksDropdown.prototype, "scrollOffset", void 0);
__decorate([
    state()
], BlocksDropdown.prototype, "isEmpty", void 0);
__decorate([
    state()
], BlocksDropdown.prototype, "selectedItemLabel", void 0);
__decorate([
    state()
], BlocksDropdown.prototype, "inputChange", void 0);
__decorate([
    query('.cat-c-dropdown__panel')
], BlocksDropdown.prototype, "_dropdownPanel", void 0);
__decorate([
    query('cat-tooltip-label-overflow')
], BlocksDropdown.prototype, "_tooltipLabelOverflow", void 0);
__decorate([
    query('.cat-c-dropdown__select')
], BlocksDropdown.prototype, "dropdownInput", void 0);
__decorate([
    query('.cat-c-dropdown__body')
], BlocksDropdown.prototype, "dropdownBody", void 0);
if (customElements.get('cat-dropdown') === undefined) {
    customElements.define('cat-dropdown', BlocksDropdown);
}
